/**
 * A coarse hardware profile of the machine the viewer is running on: which GPU actually
 * backs WebGL, and roughly how much memory the browser will admit to.
 *
 * "Was the reporter's laptop falling back to software rendering?" is the first question a
 * slow-viewer report raises and, until this existed, nothing anywhere recorded the answer —
 * not analytics, not Sentry. A software renderer (SwiftShader / llvmpipe / "Google
 * SwiftShader") and a discrete GPU produce wildly different viewer experiences on identical
 * study data, so without it a slow read is indistinguishable from a slow machine.
 *
 * PHI posture: hardware strings only. Nothing here describes the user, the patient, or the
 * study — a renderer string and a memory bucket the browser already hands to any script on
 * the page.
 */
/** Captured once per session: creating a WebGL context is not free and the answer can't change. */
let cached = null;
/**
 * Read the unmasked renderer via `WEBGL_debug_renderer_info`. Every step is optional and
 * every failure degrades to `'unavailable'` rather than throwing: the extension is absent in
 * Firefox with `privacy.resistFingerprinting`, `getContext` returns null where WebGL is
 * blocklisted, and a headless/jsdom environment has no canvas implementation at all.
 */
function readWebglRenderer() {
    try {
        const canvas = document.createElement('canvas');
        const gl = (canvas.getContext('webgl2') ??
            canvas.getContext('webgl'));
        if (!gl)
            return 'unavailable';
        try {
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            const renderer = debugInfo
                ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
                : gl.getParameter(gl.RENDERER);
            return typeof renderer === 'string' && renderer.trim() ? renderer.trim() : 'unavailable';
        }
        finally {
            // Hand the context straight back. Browsers cap live WebGL contexts per page (~16) and
            // evict the oldest when the cap is hit — the viewport contexts are exactly what would
            // be evicted, so a probe that lingers could cost a rendered viewport.
            const loseContext = gl.getExtension('WEBGL_lose_context');
            loseContext?.loseContext();
        }
    }
    catch {
        return 'unavailable';
    }
}
/**
 * The session's device profile, computed on first call and memoized thereafter.
 *
 * Property names are chosen to survive the analytics sanitizer's PHI key denylist
 * (`backend/WebApi/src/Services/Analytics/AnalyticsSanitizer.cs`), which drops any key
 * containing "name", "desc", "text", … — `gpu` and `deviceMemoryGb` contain none of them.
 */
export function getDeviceProfile() {
    if (cached)
        return cached;
    let deviceMemoryGb;
    try {
        const reported = navigator.deviceMemory;
        deviceMemoryGb = typeof reported === 'number' && reported > 0 ? reported : undefined;
    }
    catch {
        deviceMemoryGb = undefined;
    }
    cached = { gpu: readWebglRenderer(), deviceMemoryGb };
    return cached;
}
